import { RequestService } from 'Utils/Api';
import { FREE_TRIAL_PROMO } from 'Utils/Constants';
import { getPromoCode, trackPaymentError } from 'Utils/PaymentMethods';
import { logger } from 'Utils/Logger';
import { getDefaultConfig } from 'Utils/Store';
import { getSubscriptionParameters, getChangeSubscriptionType } from 'Utils/Subscriptions';
import { getPaymentTotalDue } from 'Utils/PaymentTotalDue';

const APPLE_PAY_VERSION_NUMBER = 6;

export const isApplePayAvailable = () => {
  const deviceCheck = { deviceEnabled: false, isAvailable: false };
  try {
    const device = window.ApplePaySession;
    if (device) {
      deviceCheck.deviceEnabled = true;
    }
  } catch (error) {
    return deviceCheck;
  }

  try {
    const available = window.ApplePaySession.canMakePayments();
    if (available) {
      deviceCheck.isAvailable = true;
    }
  } catch (error) {
    return deviceCheck;
  }
  return deviceCheck;
};

const applePayApi = async (url, payload) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('applePayApi', { url, payload });
  try {
    const res = await RequestService.send({
      url: `${foxApi}/applepay${url}`,
      method: 'POST',
      data: payload,
      authenticate: true,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    logger.apiResponse('applePayApi', res);
    return res && res.data;
  } catch (error) {
    logger.apiError('applePayApi', error);
    throw Error('ApplePayApi Error');
  }
};

const performValidation = (url, domain) => {
  //logging is performed in applePayApi()
  const { applePayMerchantId } = getDefaultConfig();
  const data = {
    url,
    body: {
      merchantIdentifier: applePayMerchantId,
      displayName: 'Fox',
      initiative: 'web',
      initiativeContext: domain,
    },
  };
  return applePayApi('/paymentSession', data);
};

export const applePayment = (appleData, payload) => {
  // logging is performed in applePayApi()
  let changeType;
  const { isSkipDefaultPromo, plans, promoCode, purchaseMode, subscription } = payload;
  const { selectedPlanId, selectedPlan } = plans;
  const { promotionID } = selectedPlan;
  const { isChangePlan } = purchaseMode;
  const couponCode = getPromoCode(promoCode);
  // {} object to avoid adding redundant "Operation Type" param.
  // BE also adds this param before proxying request to Evergent
  const parameters = getSubscriptionParameters({ autoCouponCode: !!promoCode.emailCoupon });

  const endpoint = isChangePlan ? '/changeService' : '/subscription';
  // if change plan, determine upgrade or downgrade change type operation.
  if (isChangePlan) {
    changeType = getChangeSubscriptionType({ selectedPlan, subscription });
  }

  const service = {
    couponCode,
    promotionId: promotionID,
    serviceType: 'PACKAGE',
    serviceId: selectedPlanId,
    quantity: '1',
    changeType,
  };

  const data = {
    ...appleData,
    service,
    isSkipPromoCode: isSkipDefaultPromo,
    parameters,
  };
  return applePayApi(endpoint, data);
};

const getPaymentRequest = async (payload) => {
  const { checkout, isSkipDefaultPromo, plans, promoCode, subscription } = payload;
  const { selectedPlan, selectedPlanPrice } = plans;
  const { freePromo, hasMerchandise, hasFreePromo } = selectedPlan;
  const { totalTax } = checkout;
  const { isNewUser, isChurnedUser } = subscription;

  let totalPrice = (selectedPlanPrice + totalTax).toFixed(2) || 0;

  const defaultTotalLabel = getPaymentTotalDue({ selectedPlan, subscription, promoCode });

  // Additional subtotals dislayed above total if promo's are available
  const items = [
    {
      label: defaultTotalLabel,
      type: 'final',
      amount: selectedPlanPrice,
    },
  ];

  if (promoCode.isValid) {
    const priceWithPromoCode = promoCode.priceToBeCharged > 0 ? promoCode.priceToBeCharged : 0;
    const difference = (priceWithPromoCode - selectedPlanPrice).toFixed(2) || 0;
    totalPrice = (priceWithPromoCode + totalTax).toFixed(2) || 0;

    items.push({
      label: promoCode.promoDescription || 'Promotion',
      type: 'final',
      amount: difference,
    });
  } else if (!isSkipDefaultPromo && hasFreePromo && (isNewUser || isChurnedUser)) {
    const { amount, displayName, promotionID } = freePromo;
    const priceToCharge = amount || 0;
    const label = FREE_TRIAL_PROMO === promotionID ? displayName : 'Promotion';
    totalPrice = (priceToCharge + totalTax).toFixed(2) || 0;

    items.push({
      label,
      type: 'final',
      amount: priceToCharge,
    });
  }

  const requiredShippingFields = hasMerchandise ? ['email', 'postalAddress'] : ['email'];

  return {
    currencyCode: 'USD',
    countryCode: 'US',
    lineItems: items,
    total: {
      label: 'Fox Today (plus applicable taxes)',
      amount: totalPrice,
    },
    requiredShippingContactFields: requiredShippingFields,
    requiredBillingContactFields: ['postalAddress'],
    supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
    merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
  };
};

const getOnValidateMerchant = (session, payload) => {
  return async (event) => {
    logger.started('applepay merchant validation', payload);

    try {
      const res = await performValidation(event.validationURL, window.location.hostname);
      logger.completed('applepay merchant validation', res);
      session.completeMerchantValidation(res);
    } catch (error) {
      logger.error('applepay merchant validation', payload, error);
      console.error('applepay merchant validation error: ', JSON.stringify(error));
      const { user } = payload;
      const tracking = {
        trackPaymentMethod: 'apple pay',
        errorMessage: error.message,
        user,
      };
      trackPaymentError(tracking);
      session.abort();
    }
  };
};

const getOnPaymentAuth = (resolve, reject, session, payload) => {
  return async (event) => {
    logger.started('applepay subscription', { event: event.payment, payload });

    try {
      const res = await applePayment(event.payment, payload);
      logger.completed('applepay subscription', res);
      if (!res || !res.orderId) {
        throw Error('payment service failure: orderId missing on payment success');
      }
      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      return resolve({ isSuccess: true, orderID: res.orderId });
    } catch (error) {
      logger.error('applepay subscription', payload, error);
      console.error('applepay payment error: ', error);
      const { user } = payload;
      const tracking = {
        trackPaymentMethod: 'apple pay',
        errorMessage: error.message,
        user,
      };
      trackPaymentError(tracking);
      session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      return reject({ isSuccess: false, errorCode: '', errorMessage: error.message });
    }
  };
};

const getOnCancel = (resolve) => {
  return () => {
    console.log('ApplePay session canceled');
    resolve();
  };
};

export const applepaySubscription = (payload) => {
  return new Promise(async (resolve, reject) => {
    const paymentRequest = await getPaymentRequest(payload);
    const session = new window.ApplePaySession(APPLE_PAY_VERSION_NUMBER, paymentRequest);
    session.onvalidatemerchant = getOnValidateMerchant(session, payload);
    session.onpaymentauthorized = getOnPaymentAuth(resolve, reject, session, payload);
    session.oncancel = getOnCancel(resolve);
    session.begin();
  });
};
